import React, { useState } from 'react';
import InfoTooltip from '@ajat/design-system/InfoTooltip';
import Button from '@ajat/design-system/Button';
import { InfoButton } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow } from '../../shared';

export const meta = {
  component: InfoTooltip,
  componentName: 'InfoTooltip',
  title: 'Info Tooltip',
  group: 'Overlays',
  importPath: '@ajat/design-system/InfoTooltip',
  description:
    'Generic popup for explanatory content — a centred card on desktop, a bottom sheet on mobile. It carries no chrome of its own beyond the close affordance, so the caller supplies the whole body. Used for the add-on hints on product pages.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires on the overlay, the close button and Escape'],
    ['children', 'node', '—', 'Tooltip body'],
    ['ariaLabel', 'string', "''", 'Accessible name for the dialog — pass the hint title'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand.',
  usage: [
    {
      title: 'Add-on hint on a product page',
      code: `import InfoTooltip from '@ajat/design-system/InfoTooltip';

const [attrInfo, setAttrInfo] = useState(null);

<InfoButton onClick={() => setAttrInfo(addon)} />

<InfoTooltip
  isOpen={Boolean(attrInfo)}
  onClose={() => setAttrInfo(null)}
  ariaLabel={attrInfo?.title || ''}
>
  {attrInfo && (
    <div
      className="pp-attr-hint typo-body"
      dangerouslySetInnerHTML={{ __html: attrInfo.html }}
    />
  )}
</InfoTooltip>`,
    },
  ],
};

const HINT = {
  title: 'Navnetryk',
  body: 'Navnetryk placeres på brystet i den valgte trykfarve. Maks. 16 tegn — vi bruger kun store bogstaver.',
};

export default function InfoTooltipPage() {
  const [open, setOpen] = useState(false);
  const [withImage, setWithImage] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live" hint="desktop card, mobile bottom sheet">
        <DemoRow align="center">
          <Button size="secondary" variant="main" onClick={() => setOpen(true)}>Open tooltip</Button>
          <Button size="secondary" variant="outline" onClick={() => setWithImage(true)}>With an illustration</Button>
        </DemoRow>

        <InfoTooltip isOpen={open} onClose={() => setOpen(false)} ariaLabel={HINT.title}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-small-l)' }}>
            <h3 className="typo-h4" style={{ color: 'var(--color-gray-10)' }}>{HINT.title}</h3>
            <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>{HINT.body}</p>
          </div>
        </InfoTooltip>

        <InfoTooltip isOpen={withImage} onClose={() => setWithImage(false)} ariaLabel="Placering af tryk">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-s)' }}>
            <h3 className="typo-h4" style={{ color: 'var(--color-gray-10)' }}>Placering af tryk</h3>
            <div
              style={{
                aspectRatio: '4 / 3',
                background: 'var(--color-gray-05)',
                borderRadius: 'var(--radius-card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span className="typo-label" style={{ color: 'var(--color-gray-5)' }}>ILLUSTRATION</span>
            </div>
            <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
              The body is entirely the caller&apos;s — headings, images and raw HTML all work.
            </p>
          </div>
        </InfoTooltip>
      </DemoCard>

      <DemoCard title="Paired with InfoButton" hint="the usual trigger in the configurator">
        <DemoRow align="center">
          <span className="typo-h4" style={{ color: 'var(--color-gray-10)' }}>Navnetryk</span>
          <InfoButton onClick={() => setOpen(true)} />
        </DemoRow>
      </DemoCard>
    </ComponentPage>
  );
}
